/* eslint-disable @typescript-eslint/no-explicit-any */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../store'
import {
  Admin,
  ApiKey,
  AuditLog,
  BillingInfo,
  Plan,
  WebhookPayload,
  WebhookSecret,
  Scim,
  Mdm,
  EmailPreferences,
  NotificationPreferences,
  EnrollEmailTemplate,
  ThreatHunterUser,
  ConditionalAccessResponse,
  DevicePolicy,
  CustomDnsConfiguration,
} from '../../types/SettingsType'

export interface SettingsState {
  currentPage: string
  admins: Admin[] | undefined
  auditLogs: AuditLog[] | undefined
  billingInfo: BillingInfo | undefined
  planInfo: Plan | undefined
  apiKey: ApiKey | undefined
  webhooks: WebhookPayload[] | undefined
  webhookSecrets: WebhookSecret[] | undefined
  conditionalAccess: ConditionalAccessResponse | undefined
  conditionalAccessStatus: boolean | undefined
  devicePolicies: DevicePolicy[] | undefined
  devicePolicy: DevicePolicy | undefined
  scim: Scim | undefined
  mdm: Mdm | undefined
  emailPreference: EmailPreferences | undefined
  notificationPreference: NotificationPreferences | undefined
  emailEnrollmentTemplate: EnrollEmailTemplate | undefined
  threatHunterUsers: ThreatHunterUser[] | undefined
  auditLogsTotalPages: number
  auditLogsCurrentPage: number
  orgHasDemoData: boolean
  customDnsConfiguration: CustomDnsConfiguration | undefined
  mtdStatus: boolean | undefined
}

const initialState: SettingsState = {
  currentPage: 'SSO',
  admins: undefined,
  auditLogs: undefined,
  billingInfo: undefined,
  planInfo: undefined,
  apiKey: undefined,
  webhooks: undefined,
  webhookSecrets: undefined,
  conditionalAccess: undefined,
  conditionalAccessStatus: undefined,
  devicePolicies: undefined,
  devicePolicy: undefined,
  scim: undefined,
  mdm: undefined,
  emailPreference: undefined,
  notificationPreference: undefined,
  emailEnrollmentTemplate: undefined,
  threatHunterUsers: undefined,
  auditLogsTotalPages: 1,
  auditLogsCurrentPage: 0,
  orgHasDemoData: false,
  customDnsConfiguration: undefined,
  mtdStatus: undefined,
}

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setCurrentPage(state, action: PayloadAction<any>) {
      state.currentPage = action.payload
    },
    setAdmins(state, action: PayloadAction<any>) {
      state.admins = action.payload
    },
    setAuditLogs(state, action: PayloadAction<any>) {
      state.auditLogs = action.payload
    },
    setBillingInfo(state, action: PayloadAction<any>) {
      state.billingInfo = action.payload
    },
    setPlanInfo(state, action: PayloadAction<any>) {
      state.planInfo = action.payload
    },
    setApiKey(state, action: PayloadAction<any>) {
      state.apiKey = action.payload
    },
    setWebhooks(state, action: PayloadAction<any>) {
      state.webhooks = action.payload
    },
    setWebhookSecrets(state, action: PayloadAction<any>) {
      state.webhookSecrets = action.payload
    },
    setConditionalAccess(state, action: PayloadAction<any>) {
      state.conditionalAccess = action.payload
    },
    setConditionalAccessStatus(state, action: PayloadAction<any>) {
      state.conditionalAccessStatus = action.payload
    },
    setMtdStatus(state, action: PayloadAction<any>) {
      state.mtdStatus = action.payload
    },
    setDevicePolicies(state, action: PayloadAction<any>) {
      state.devicePolicies = action.payload
    },
    setDevicePolicy(state, action: PayloadAction<any>) {
      state.devicePolicy = action.payload
    },
    setScim(state, action: PayloadAction<any>) {
      state.scim = action.payload
    },
    setMdm(state, action: PayloadAction<any>) {
      state.mdm = action.payload
    },
    setEmailPreference(state, action: PayloadAction<any>) {
      state.emailPreference = action.payload
    },
    setNotificationPreference(state, action: PayloadAction<any>) {
      state.notificationPreference = action.payload
    },
    setEmailEnrollmentTemplate(state, action: PayloadAction<any>) {
      state.emailEnrollmentTemplate = action.payload
    },
    setCustomDnsConfiguration(state, action: PayloadAction<CustomDnsConfiguration | undefined>) {
      state.customDnsConfiguration = action.payload
    },
    setThreatHunterUsers(state, action: PayloadAction<any>) {
      state.threatHunterUsers = action.payload
    },
    setAuditLogsTotalPages(state, action: PayloadAction<any>) {
      state.auditLogsTotalPages = action.payload
    },
    setAuditLogsCurrentPage(state, action: PayloadAction<any>) {
      state.auditLogsCurrentPage = action.payload
    },
    setOrgHasDemoData(state, action: PayloadAction<any>) {
      state.orgHasDemoData = action.payload
    },
  },
})
export const selectSettings = (state: RootState) => state.settings

export const {
  setCurrentPage,
  setAdmins,
  setAuditLogs,
  setBillingInfo,
  setPlanInfo,
  setApiKey,
  setWebhooks,
  setWebhookSecrets,
  setScim,
  setMdm,
  setEmailPreference,
  setNotificationPreference,
  setEmailEnrollmentTemplate,
  setThreatHunterUsers,
  setConditionalAccess,
  setConditionalAccessStatus,
  setMtdStatus,
  setDevicePolicies,
  setDevicePolicy,
  setAuditLogsTotalPages,
  setAuditLogsCurrentPage,
  setOrgHasDemoData,
  setCustomDnsConfiguration,
} = settingsSlice.actions
export default settingsSlice.reducer
